'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SystemSetting {
  id: string;
  category: string;
  name: string;
  description: string;
  value: string | boolean | number;
  type: 'toggle' | 'select' | 'number' | 'text';
  options?: string[];
}

const mockSettings: SystemSetting[] = [
  {
    id: '1',
    category: 'Operations',
    name: 'Auto-Assignment',
    description: 'Automatically assign new shipments to available drivers',
    value: true,
    type: 'toggle',
  },
  {
    id: '2',
    category: 'Operations',
    name: 'Priority Routing',
    description: 'Enable priority-based route optimization',
    value: true,
    type: 'toggle',
  },
  {
    id: '3',
    category: 'Notifications',
    name: 'Email Notifications',
    description: 'Send automated email updates to customers',
    value: true,
    type: 'toggle',
  },
  {
    id: '4',
    category: 'Notifications',
    name: 'Alert Frequency',
    description: 'How often to check for critical alerts',
    value: '5',
    type: 'select',
    options: ['1', '5', '10', '15', '30'],
  },
  {
    id: '5',
    category: 'Performance',
    name: 'Max Active Shipments',
    description: 'Maximum shipments per driver',
    value: 8,
    type: 'number',
  },
  {
    id: '6',
    category: 'Performance',
    name: 'Delivery Time Window',
    description: 'Standard delivery window in hours',
    value: 24,
    type: 'number',
  },
];

export default function OperationalControls() {
  const [settings, setSettings] = useState<SystemSetting[]>(mockSettings);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = ['all', ...Array.from(new Set(settings.map((s) => s.category)))];

  const filteredSettings = settings.filter(
    (setting) => activeCategory === 'all' || setting.category === activeCategory
  );

  const updateSetting = (id: string, newValue: string | boolean | number) => {
    setSettings(settings.map((s) => (s.id === id ? { ...s, value: newValue } : s)));
  };

  return (
    <div className="space-y-6">
      {/* Control Panel Header */}
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-6 border border-border">
        <div className="flex items-center gap-3 mb-2">
          <Icon name="Cog6ToothIcon" size={32} className="text-primary" />
          <h2 className="text-2xl font-bold text-foreground">Operational Controls</h2>
        </div>
        <p className="text-muted-foreground">
          Manage system settings, automation rules, and operational parameters
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button className="bg-card rounded-xl p-6 shadow-card border border-border hover:shadow-lg transition-all text-left group">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Icon name="ArrowPathIcon" size={24} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Sync Database</h3>
              <p className="text-sm text-muted-foreground">Last sync: 5 min ago</p>
            </div>
          </div>
        </button>
        <button className="bg-card rounded-xl p-6 shadow-card border border-border hover:shadow-lg transition-all text-left group">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
              <Icon name="DocumentArrowDownIcon" size={24} className="text-secondary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Export Reports</h3>
              <p className="text-sm text-muted-foreground">Generate analytics</p>
            </div>
          </div>
        </button>
        <button className="bg-card rounded-xl p-6 shadow-card border border-border hover:shadow-lg transition-all text-left group">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
              <Icon name="ShieldCheckIcon" size={24} className="text-accent" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">System Backup</h3>
              <p className="text-sm text-muted-foreground">Last backup: 2 hours ago</p>
            </div>
          </div>
        </button>
      </div>

      {/* Category Filter */}
      <div className="bg-card rounded-xl p-4 shadow-card border border-border">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${
                activeCategory === category
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Settings List */}
      <div className="space-y-4">
        {filteredSettings.map((setting) => (
          <div key={setting.id} className="bg-card rounded-xl p-6 shadow-card border border-border">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">{setting.name}</h3>
                  <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {setting.category}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{setting.description}</p>
              </div>

              {/* Control Input */}
              <div className="flex-shrink-0">
                {setting.type === 'toggle' && (
                  <button
                    onClick={() => updateSetting(setting.id, !setting.value)}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                      setting.value ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        setting.value ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                )}

                {setting.type === 'select' && (
                  <select
                    value={setting.value as string}
                    onChange={(e) => updateSetting(setting.id, e.target.value)}
                    className="px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground min-w-[120px]"
                  >
                    {setting.options?.map((option) => (
                      <option key={option} value={option}>
                        {option} {setting.name.includes('Frequency') ? 'min' : ''}
                      </option>
                    ))}
                  </select>
                )}

                {setting.type === 'number' && (
                  <input
                    type="number"
                    value={setting.value as number}
                    onChange={(e) => updateSetting(setting.id, parseInt(e.target.value))}
                    className="px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground w-24 text-center"
                  />
                )}

                {setting.type === 'text' && (
                  <input
                    type="text"
                    value={setting.value as string}
                    onChange={(e) => updateSetting(setting.id, e.target.value)}
                    className="px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground min-w-[200px]"
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* System Status */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">System Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-4 bg-success/5 rounded-lg border border-success/20">
            <Icon name="CheckCircleIcon" size={24} className="text-success" />
            <div>
              <p className="text-sm font-medium text-foreground">Database</p>
              <p className="text-xs text-muted-foreground">Operational</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-success/5 rounded-lg border border-success/20">
            <Icon name="CheckCircleIcon" size={24} className="text-success" />
            <div>
              <p className="text-sm font-medium text-foreground">API Services</p>
              <p className="text-xs text-muted-foreground">All systems go</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-success/5 rounded-lg border border-success/20">
            <Icon name="CheckCircleIcon" size={24} className="text-success" />
            <div>
              <p className="text-sm font-medium text-foreground">Email Service</p>
              <p className="text-xs text-muted-foreground">Connected</p>
            </div>
          </div>
        </div>
      </div>

      {/* Save Changes */}
      <div className="flex justify-end gap-4">
        <button className="px-6 py-3 border border-input rounded-lg hover:bg-muted transition-colors text-foreground font-medium">
          Reset to Defaults
        </button>
        <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium">
          Save Changes
        </button>
      </div>
    </div>
  );
}
